import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Container,
    Grid,
    LinearProgress,
    Paper,
    Snackbar,
    Stack,
    Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

import { createAsset, deleteAsset, getAssets, updateAsset } from "../../api";
import AssetFormDialog from "./AssetFormDialog";
import { logError } from "../../logging/loggingService";

const fmtCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(value));
};

export default function AssetManagement() {
    const [loading, setLoading] = useState(true);
    const [assets, setAssets] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState("create");
    const [dialogAsset, setDialogAsset] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const [deleteBusy, setDeleteBusy] = useState(null);

    const loadAssets = useCallback(() => {
        setLoading(true);
        getAssets()
            .then((res) => {
                setAssets(Array.isArray(res.data) ? res.data : []);
            })
            .catch((err) => {
                logError("Fehler beim Laden der Anlagegüter", err, { component: "AssetManagement" });
                setSnack({ open: true, msg: "Anlagegüter konnten nicht geladen werden", sev: "error" });
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    const handleOpenCreate = useCallback(() => {
        setDialogMode("create");
        setDialogAsset({
            method: "LINEAR",
            usefulLifeYears: 3,
            residualValue: 0,
            accountNumber: "",
        });
        setDialogError(null);
        setDialogOpen(true);
    }, []);

    const handleOpenEdit = useCallback((asset) => {
        setDialogMode("edit");
        setDialogAsset(asset);
        setDialogError(null);
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        if (saving) return;
        setDialogOpen(false);
        setDialogAsset(null);
        setDialogError(null);
    }, [saving]);

    const handleSave = useCallback(async (payload) => {
        setSaving(true);
        setDialogError(null);
        try {
            if (dialogMode === "edit" && dialogAsset?.id) {
                await updateAsset(dialogAsset.id, payload);
                setSnack({ open: true, msg: "Anlagegut aktualisiert", sev: "success" });
            } else {
                await createAsset(payload);
                setSnack({ open: true, msg: "Anlagegut angelegt", sev: "success" });
            }
            setDialogOpen(false);
            setDialogAsset(null);
            loadAssets();
        } catch (err) {
            logError("Fehler beim Speichern des Anlageguts", err, { component: "AssetManagement" });
            setDialogError(
                err?.message || "Anlagegut konnte nicht gespeichert werden. Bitte Eingaben prüfen."
            );
        } finally {
            setSaving(false);
        }
    }, [dialogAsset, dialogMode, loadAssets, setSnack]);

    const handleDelete = useCallback(async (asset) => {
        if (!asset?.id) return;
        const confirmed = window.confirm(`Anlagegut "${asset.name}" wirklich löschen?`);
        if (!confirmed) return;
        setDeleteBusy(asset.id);
        try {
            await deleteAsset(asset.id);
            setSnack({ open: true, msg: "Anlagegut gelöscht", sev: "success" });
            loadAssets();
        } catch (err) {
            logError("Fehler beim Löschen des Anlageguts", err, { component: "AssetManagement" });
            setSnack({ open: true, msg: "Anlagegut konnte nicht gelöscht werden", sev: "error" });
        } finally {
            setDeleteBusy(null);
        }
    }, [loadAssets, setSnack]);

    const cards = useMemo(() => {
        if (!Array.isArray(assets)) {
            return [];
        }
        return assets
            .slice()
            .sort((a, b) => {
                const dateA = a.acquisitionDate ? new Date(a.acquisitionDate).getTime() : 0;
                const dateB = b.acquisitionDate ? new Date(b.acquisitionDate).getTime() : 0;
                return dateB - dateA;
            })
            .map((asset) => {
                const acquisitionDate = asset.acquisitionDate
                    ? new Date(asset.acquisitionDate).toLocaleDateString("de-DE")
                    : "—";
                const invoiceDate = asset.invoiceDate
                    ? new Date(asset.invoiceDate).toLocaleDateString("de-DE")
                    : null;
                return (
                    <Grid item key={asset.id || asset.name} xs={12} sm={6} md={4}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                boxShadow: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "0 6px 24px rgba(0,0,0,0.45)"
                                        : "0 6px 24px rgba(0,0,0,0.06)",
                                border: (theme) =>
                                    `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#e0f2f1"}`,
                                background: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? theme.palette.background.paper
                                        : "linear-gradient(0deg, #e0f7fa 0%, #ffffff 60%)",
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {asset.name}
                                    </Typography>
                                    <Chip label={`${asset.usefulLifeYears} Jahre`} size="small" color="primary" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Anschaffung: {acquisitionDate}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Anschaffungswert: {fmtCurrency(asset.acquisitionValue)}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Konto: {asset.accountNumber || "—"}
                                </Typography>
                                {asset.residualValue !== null && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Restwert: {fmtCurrency(asset.residualValue)}
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Methode: {asset.method}
                                </Typography>
                                {(asset.invoiceNumber || invoiceDate) && (
                                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Verknüpfter Beleg
                                        </Typography>
                                        {asset.invoiceNumber && (
                                            <Typography variant="body2">
                                                Nummer: {asset.invoiceNumber}
                                            </Typography>
                                        )}
                                        {invoiceDate && (
                                            <Typography variant="body2">
                                                Datum: {invoiceDate}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </CardContent>
                            <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<EditRoundedIcon />}
                                        onClick={() => handleOpenEdit(asset)}
                                    >
                                        Bearbeiten
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        startIcon={<DeleteRoundedIcon />}
                                        onClick={() => handleDelete(asset)}
                                        disabled={deleteBusy === asset.id}
                                    >
                                        Löschen
                                    </Button>
                                </Stack>
                            </CardActions>
                        </Card>
                    </Grid>
                );
            });
    }, [assets, deleteBusy, handleOpenEdit, handleDelete]);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        Anlagegüter
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Erfasse Investitionen, verknüpfe Belege und bereite die AfA vor.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={handleOpenCreate}
                    sx={{ backgroundColor: "#00838f", "&:hover": { backgroundColor: "#006064" } }}
                >
                    Anlagegut anlegen
                </Button>
            </Stack>

            {loading && (
                <Box sx={{ mb: 3 }}>
                    <LinearProgress />
                </Box>
            )}

            {!loading && assets.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 3, md: 6 },
                        borderRadius: 4,
                        textAlign: "center",
                        borderColor: "#b2ebf2",
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Noch keine Anlagegüter erfasst
                    </Typography>
                    <Typography sx={{ mt: 1.5, opacity: 0.8 }}>
                        Lege deine ersten Investitionen an und verknüpfe sie mit den passenden Belegen.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleOpenCreate}
                        sx={{
                            mt: 3,
                            backgroundColor: "#00838f",
                            "&:hover": { backgroundColor: "#006064" },
                        }}
                    >
                        Jetzt starten
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {cards}
                </Grid>
            )}

            <AssetFormDialog
                open={dialogOpen}
                title={dialogMode === "edit" ? "Anlagegut bearbeiten" : "Anlagegut anlegen"}
                initialData={dialogAsset}
                onClose={handleCloseDialog}
                onSave={handleSave}
                saving={saving}
                error={dialogError}
            />

            <Snackbar
                open={snack.open}
                autoHideDuration={6000}
                onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                    severity={snack.sev || "success"}
                    sx={{ width: "100%" }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Container>
    );
}
